import { bind } from 'bind-event-listener';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { PageElementCounts } from '@atlaskit/editor-common/utils';
import { findNodePosByLocalIds, getPageElementCounts } from '@atlaskit/editor-common/utils';
import type { EditorActions } from '@atlaskit/editor-core';
import { ToolbarPortalContextProvider } from '@atlaskit/editor-core';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import type { SelectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { NodeSelection, Selection } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { insm } from '@atlaskit/insm';

import { useEditorAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useEditorAnnotationProviderOptions.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useUpdateAnnotations } from '@confluence/comments-data/entry-points/useUpdateAnnotations.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import type { LegacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import { OuterEditorViewContext } from '@confluence/fabric-extension-handlers/entry-points/OuterEditorViewContext.ts';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments.ts';
import { usePageContentId } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';

import { ToolbarPortal } from './ToolbarPortal.tsx';
import { defaultDocument } from './default-document.ts';
import { NBMEditor } from './editor/NBMEditor.tsx';
import { useNBMEditorPreset } from './editor/useNBMEditorPreset.ts';
import { withEditorActions } from './editor/withEditorActions.tsx';
import type { LegacyContentMacroParams } from './legacy-content-macro-params.ts';
import { useNBMFocus } from './useNBMFocus.ts';
import { ReadOnlyLCM } from './ReadOnlyLCM.tsx';

export interface ExtensionHandlerProps {
	node: ExtensionParams<LegacyContentMacroParams>;
	outerEditorView: EditorView | undefined;
	outerEditorApi:
		| PublicPluginAPI<[ExtensionPlugin, LegacyContentMacroPlugin, SelectionToolbarPlugin]>
		| undefined;
	editorActions?: EditorActions;
	propOverrides?: { contentMode?: 'standard' | 'compact' | undefined };
}

const ExtensionHandler = ({
	node,
	outerEditorView,
	outerEditorApi,
	editorActions,
	propOverrides,
}: ExtensionHandlerProps) => {
	const wrapperRef = useRef<HTMLDivElement>(null);

	// clean up platform_editor_user_preference_override cleanup: remove initialToolbarDockingRef
	const initialToolbarDockingRef = useRef<'top' | 'none' | undefined>(undefined);

	const { onNbmBlur, onNbmFocus, nbmFocused } = useNBMFocus(wrapperRef);

	const lastClickInsideLCM = useRef<boolean>(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const innerEditorView = useRef<EditorView | null>(null);

	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const madeChangesLocally = useRef<boolean>(false);
	const [contentId] = usePageContentId();

	const setInnerEditorView = (editorView: EditorView) => {
		innerEditorView.current = editorView;
	};

	// These plugins are used in dependency arrays and were constantly triggering useEffects.
	const memoedOuterPlugins = useMemo(() => {
		return {
			lcm: outerEditorApi?.legacyContentMacroPlugin,
			extension: outerEditorApi?.extension,
		};
	}, [outerEditorApi]);

	const setSelection = useCallback(
		(editorView: EditorView, selection?: Selection) => {
			const { state, dispatch } = editorView;
			return memoedOuterPlugins.lcm?.actions.setSelection(selection)(state, dispatch);
		},
		[memoedOuterPlugins.lcm],
	);

	// Don't want this to constantly update and retrigger the initialise analytics event.
	const initialDocumentContent = useRef<PageElementCounts | null>(
		(node?.parameters?.nestedContent && getPageElementCounts(node.parameters.nestedContent)) ||
			null,
	);

	const { updateUnresolvedInlineComment } = useInlineCommentsActions();
	const { setOrderedActiveAnnotationIdList } = useCommentsDataActions();

	const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
		updateUnresolvedInlineComment,
		setOrderedActiveAnnotationIdList,
	]);

	const annotationProvider = useEditorAnnotationProviderOptions({
		pageId: contentId ?? '',
		editorActions,
		triggerAnalyticsEvent: createAnalyticsEvent,
		updateUnresolvedInlineComment: updateUnresolvedInlineCommentFn,
	});

	useEffect(() => {
		if (contentId && node.localId && initialDocumentContent.current) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'editorInitialised',
					actionSubject: 'legacyContentMacro',
					source: 'legacyContentMacro',
					attributes: {
						nodeId: node.localId,
						objectId: contentId,
						pageElementCounts: initialDocumentContent.current,
					},
				},
			}).fire();
		}
	}, [contentId, createAnalyticsEvent, node.localId]);

	useEffect(() => {
		// On first load, the default selection pick the extension node
		// if it is the first node and triggering the floating toolbar.
		// Reset the selection to prevent the toolbar from appearing automatically.
		if (innerEditorView.current) {
			setSelection(innerEditorView.current);
		}
	}, [setSelection]);

	useEffect(() => {
		const unsubOnChange = memoedOuterPlugins.lcm?.sharedState.onChange(
			({ prevSharedState, nextSharedState }: any) => {
				// Only update the selection when we notice a key press that would move the cursor inside the legacy content macro
				if (
					prevSharedState?.arrowKeyPressed !== nextSharedState?.arrowKeyPressed &&
					nextSharedState?.nodeId === node.localId
				) {
					// Listen to changes on the arrowKeyPressed value to know when to arrow navigate IN to the LCM.
					const updatedArrowNavValue = nextSharedState?.arrowKeyPressed;
					const currentInnerEditorView = innerEditorView.current;

					if (!currentInnerEditorView || !updatedArrowNavValue || !outerEditorView) {
						return;
					}

					// Reset the outer editor's selection
					setSelection(outerEditorView);

					// Focusing the inner editor is sufficient to get the selection at pos 0 when arrowing right,
					// but we've to explicitly set it to end of the doc on arrow left & arrow up.
					// This puts the cursor in the first or last valid text selection in the document. It’s a
					// bit weird for extensions but it’s what we do for the editor itself. This avoids an
					// awkward selection situation where the first position isn’t a valid text selection (eg.
					// when it’s a gap cursor which it will be for a lot of of encapsulated elements).
					let selection;
					if (updatedArrowNavValue === 'ArrowLeft' || updatedArrowNavValue === 'ArrowUp') {
						selection = Selection.atEnd(currentInnerEditorView.state.doc);
					} else {
						// Set selection to start of document in LCM
						selection = Selection.atStart(currentInnerEditorView.state.doc);
					}
					setSelection(currentInnerEditorView, selection);

					// Put the inner editor into focus.
					currentInnerEditorView.focus();
				}
			},
		);
		return unsubOnChange;
	}, [memoedOuterPlugins.lcm, outerEditorView, setSelection, node.localId]);

	const { editorApi, preset, fullPageEditorFeatureFlags } = useNBMEditorPreset({
		nodeId: node?.localId,
		annotationProvider,
	});
	// These plugins are used in dependency arrays and were constantly triggering useEffects.
	const memoedInnerPlugins = useMemo(() => {
		return {
			lcm: (editorApi as PublicPluginAPI<[LegacyContentMacroPlugin]>)?.legacyContentMacroPlugin,
			selectionToolbar: (editorApi as PublicPluginAPI<[SelectionToolbarPlugin]>)?.selectionToolbar,
		};
	}, [editorApi]);

	useEffect(() => {
		const unsubOnChange = memoedInnerPlugins.lcm?.sharedState.onChange(
			({ prevSharedState, nextSharedState }: any) => {
				// Select the LCM node when moving out of it
				if (
					prevSharedState?.isSelectionAtEdge !== nextSharedState?.isSelectionAtEdge &&
					nextSharedState?.nodeId === node.localId &&
					nextSharedState?.isSelectionAtEdge
				) {
					if (!outerEditorView || !node.localId) {
						return;
					}

					// reset inner editor selection
					if (innerEditorView.current) {
						setSelection(innerEditorView.current);
					}

					// set outer editor selection
					const lcmNode = findNodePosByLocalIds(outerEditorView.state, [node.localId])[0];
					const lcmSelection = NodeSelection.create(outerEditorView.state.doc, lcmNode.pos);
					setSelection(outerEditorView, lcmSelection);

					// Focus outer editor
					outerEditorView.focus();
				}
			},
		);
		return unsubOnChange;
	}, [memoedInnerPlugins.lcm, outerEditorView, setSelection, node.localId]);

	/**
	 * This effect is used to sync the toolbar docking state between the inner and outer editor
	 * We currently have two seperate shared states for the selection toolbar, the outer editor (Main Editor)
	 * and the inner editor (Legacy Content Macro)
	 * If you change the toolbar docking position in either editor, we need this state to be reflected in the other editor so that
	 * the toolbar is in the same place.
	 */
	useEffect(() => {
		const unsubOnChange = memoedInnerPlugins.selectionToolbar?.sharedState.onChange(
			({
				prevSharedState,
				nextSharedState,
				//
			}: {
				prevSharedState?: any;
				nextSharedState?: any;
			}) => {
				if (prevSharedState?.toolbarDocking !== nextSharedState?.toolbarDocking) {
					const updatedToolbarDocking = nextSharedState?.toolbarDocking;

					if (!outerEditorApi || !updatedToolbarDocking || !node.localId) {
						return;
					}

					if (expValEquals('platform_editor_user_preference_override', 'isEnabled', true)) {
						outerEditorApi.selectionToolbar?.actions?.overrideToolbarDocking?.(
							updatedToolbarDocking,
						);
					} else {
						outerEditorApi.selectionToolbar?.actions?.forceToolbarDockingWithoutAnalytics?.(
							updatedToolbarDocking,
						);
					}
				}
			},
		);
		return unsubOnChange;
	}, [memoedInnerPlugins.selectionToolbar, outerEditorApi, node.localId]);

	/**
	 * This effect is used to sync the toolbar docking state between the outer and inner editor
	 */
	useEffect(() => {
		const unsubOnChange = outerEditorApi?.selectionToolbar?.sharedState.onChange(
			({ prevSharedState, nextSharedState }: any) => {
				if (prevSharedState?.toolbarDocking !== nextSharedState?.toolbarDocking) {
					const updatedToolbarDocking = nextSharedState?.toolbarDocking;

					if (!outerEditorApi || !updatedToolbarDocking || !node.localId) {
						return;
					}

					if (expValEquals('platform_editor_user_preference_override', 'isEnabled', true)) {
						memoedInnerPlugins.selectionToolbar?.actions?.overrideToolbarDocking?.(
							updatedToolbarDocking,
						);
					} else {
						memoedInnerPlugins.selectionToolbar?.actions?.forceToolbarDockingWithoutAnalytics?.(
							updatedToolbarDocking,
						);
					}
				}
			},
		);
		return unsubOnChange;
	}, [outerEditorApi, memoedInnerPlugins.selectionToolbar, node.localId]);

	const throttledSaveContentChanges = useMemo(() => {
		// Save LCM content changes at most every 2 seconds.
		return throttle((updatedDoc: PMNode) => {
			const nodeLocalId = node?.localId;

			if (!nodeLocalId || !memoedOuterPlugins.extension || !outerEditorView) {
				return;
			}

			memoedOuterPlugins.extension.actions?.api()?.doc.update(
				nodeLocalId,
				(currentValue: any) => {
					const jsonUpdatedDoc: JSONDocNode = updatedDoc.toJSON();

					const nextParameters = {
						nestedContent: jsonUpdatedDoc,
						adf: JSON.stringify(jsonUpdatedDoc), // Has to be converted to JSON then stringified.
					};
					const nextValue = {
						...currentValue,
						attrs: {
							...currentValue.attrs,
							parameters: nextParameters,
						},
					};

					setHasUnsavedChanges(false);

					madeChangesLocally.current = true;

					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'autoSave',
							actionSubject: 'legacyContentMacro',
							source: 'legacyContentMacro',
							attributes: {
								nodeId: nodeLocalId,
								objectId: contentId,
							},
						},
					}).fire();

					return nextValue;
				},
				{ scrollIntoView: false },
			);

			// The LCM selects itself after saving in some cases, so this is a workaround to reset the selection.
			setSelection(outerEditorView);
		}, 2000);
	}, [
		contentId,
		createAnalyticsEvent,
		memoedOuterPlugins.extension,
		node?.localId,
		outerEditorView,
		setSelection,
	]);

	const handleContentChanges = useCallback(
		(updatedDoc: PMNode) => {
			setHasUnsavedChanges(true);

			throttledSaveContentChanges(updatedDoc);
		},
		[throttledSaveContentChanges],
	);

	useEffect(() => {
		const handleExitingLCM = (editorView: EditorView) => {
			throttledSaveContentChanges.flush(); // Force save any throttled changes.

			setSelection(editorView);
		};

		let unbindMousedownCapture: () => void;
		let unbindMousedown: () => void;
		let unbindPaste: () => void;
		let unbindBlur: () => void;
		let unbindFocus: () => void;
		let unbindBeforeUnload: () => void;

		if (wrapperRef.current) {
			unbindMousedownCapture = bind(wrapperRef.current, {
				type: 'mousedown',
				listener: () => {
					// Add a flag to indicate that the last click was inside the LCM. This is used by DOM events that follow this mousedown in order to know if they may have been triggered by a click inside the LCM. It is reset after the events have been emitted by the timeout trick.
					lastClickInsideLCM.current = true;

					// When the user clicks, double check that there's no pre-existing selection outside the LCM.
					// If there is, it is reset. When the outer selection is "empty" the head is 0.
					if (outerEditorView) {
						const doesOuterViewHaveSelection = outerEditorView.state.selection.head !== 0;

						if (doesOuterViewHaveSelection) {
							setSelection(outerEditorView);
						}
					}

					setTimeout(() => {
						lastClickInsideLCM.current = false;
					}, 0);
				},
				options: { capture: true },
			});

			unbindMousedown = bind(wrapperRef.current, {
				type: 'mousedown',
				listener: (event) => {
					// Check if the event originates from within the Dropdown component.
					// Because react-select relies on event propagation for its interactions,
					// preventing propagation in the parent container stops the dropdown from opening.
					const isDropdown = (event.target as HTMLElement).closest('.field-wrapper-enum');
					if (!isDropdown) {
						// stop propagation here to fix the double selection issue
						// where the LCM node and its inside content are both selected.
						event.stopPropagation();
					}
				},
				options: { capture: false },
			});

			unbindPaste = bind(wrapperRef.current, {
				type: 'paste',
				listener: (event) => {
					event.stopPropagation();
				},
				options: { capture: false },
			});

			unbindBlur = bind(wrapperRef.current, {
				type: 'blur',
				listener: (event) => {
					if (hasUnsavedChanges) {
						throttledSaveContentChanges.flush(); // Force save any throttled changes.
					}

					// If the last clicked element was inside the LCM, force the focus to remain inside the LCM.
					// Otherwise, the user has clicked out of the LCM and should lose focus.
					// If the click is inside the LCM and part of the editable area, don't need to handle it.
					const previousElement = event.target as HTMLElement;
					const isTargetExtensionEditableArea = !!(event.relatedTarget as HTMLElement)?.closest(
						'.extension-editable-area',
					);

					// If the user has clicked from inside the LCM to outside the LCM to the outer editor, the selection is reset.
					// relatedTarget represents the inner LCM editor, target represents the outer editor.
					// This must be checked to ensure it's not a genuine blur like opening a popup, e.g. emoji, link inserts.
					const isSelectionLeavingLCM =
						(event.relatedTarget as HTMLElement)?.id === 'ak-editor-textarea' &&
						(event.target as HTMLElement)?.id === 'ak-editor-textarea';

					if (lastClickInsideLCM.current && previousElement && !isTargetExtensionEditableArea) {
						previousElement.focus();
						lastClickInsideLCM.current = false;
					} else if (isSelectionLeavingLCM && innerEditorView.current) {
						handleExitingLCM(innerEditorView.current);
					}

					if (isSelectionLeavingLCM) {
						if (expValEquals('platform_editor_user_preference_override', 'isEnabled', true)) {
							// When leaving the LCM with the new overrideToolbarDocking API, revert the temporary override
							// clearing the override and reverting to the underlying preference
							outerEditorApi?.selectionToolbar?.actions?.clearToolbarDockingOverride?.();
						} else {
							outerEditorApi?.selectionToolbar?.actions?.forceToolbarDockingWithoutAnalytics?.(
								initialToolbarDockingRef.current ?? 'none',
							);
						}
					}

					onNbmBlur(event);
				},
				options: { capture: true },
			});

			unbindFocus = bind(wrapperRef.current, {
				type: 'focus',
				listener: () => {
					// We want to store the initial toolbar docking state when the user first focuses into the LCM
					// This way we can return the focus state to the original state when the user blurs out of the LCM
					const toolbarDocking =
						outerEditorApi?.selectionToolbar?.sharedState.currentState()?.toolbarDocking;
					if (!initialToolbarDockingRef.current) {
						if (expValEquals('platform_editor_user_preference_override', 'isEnabled', true)) {
							memoedInnerPlugins.selectionToolbar?.actions?.overrideToolbarDocking?.('top');
						} else {
							memoedInnerPlugins.selectionToolbar?.actions?.forceToolbarDockingWithoutAnalytics?.(
								'top',
							);
						}
					}
					initialToolbarDockingRef.current = toolbarDocking;
					if (expValEquals('platform_editor_user_preference_override', 'isEnabled', true)) {
						outerEditorApi?.selectionToolbar?.actions?.overrideToolbarDocking?.('top');
					} else {
						outerEditorApi?.selectionToolbar?.actions?.forceToolbarDockingWithoutAnalytics?.('top');
					}

					onNbmFocus();
				},
				options: { capture: true },
			});

			unbindBeforeUnload = bind(window, {
				type: 'beforeunload',
				listener: (event: BeforeUnloadEvent) => {
					// Prompt the user whether they actually want to reload when they've unsaved changes.
					if (hasUnsavedChanges) {
						event.preventDefault();
						event.returnValue = '';

						throttledSaveContentChanges.flush(); // Force save any throttled changes.
					}
				},
			});
		}

		return () => {
			unbindMousedownCapture?.();
			unbindMousedown?.();
			unbindPaste?.();
			unbindBlur?.();
			unbindFocus?.();
			unbindBeforeUnload?.();
		};
	}, [
		outerEditorApi?.selectionToolbar?.actions,
		outerEditorApi?.selectionToolbar?.sharedState,
		throttledSaveContentChanges,
		hasUnsavedChanges,
		setSelection,
		outerEditorView,
		onNbmBlur,
		onNbmFocus,
		memoedInnerPlugins.selectionToolbar,
	]);

	const mountPointElement = useMemo(() => {
		return window.document.querySelector('.fabric-editor-popup-scroll-parent') as HTMLElement;
	}, []);

	if (!mountPointElement) {
		throw new Error('Mountpoint does not exist for NBM editor');
	}

	// Due to the constant re-renders the editorApi object is not stable, which is why this memo was used.
	const replaceDocument = useMemo(
		() => editorApi?.core.actions.replaceDocument,
		[editorApi?.core.actions.replaceDocument],
	);

	useEffect(() => {
		// This is to make sure collab changes are reflected in the nested editor, as when the node content is passed in as defaultValue it only displays content at the time of initialisation.
		// If the content changes were made by the current user, they're already reflected on the client side so we don't need to replace the document.
		if (!madeChangesLocally.current && replaceDocument) {
			replaceDocument(node?.parameters?.adf, { skipValidation: true });
		}
		madeChangesLocally.current = false;
	}, [node?.parameters?.adf, replaceDocument]);

	return (
		<AnalyticsContext data={{ attributes: { isLegacyContentMacro: true } }}>
			<div ref={wrapperRef}>
				<ToolbarPortalContextProvider portal={ToolbarPortal} isActive={nbmFocused}>
					<NBMEditor
						editorApi={editorApi}
						preset={preset}
						featureFlags={fullPageEditorFeatureFlags}
						document={node?.parameters?.nestedContent || defaultDocument}
						boundariesElement={mountPointElement}
						mountPoint={mountPointElement}
						annotationProvider={annotationProvider}
						setInnerEditorView={setInnerEditorView}
						handleContentChanges={handleContentChanges}
						contentMode={propOverrides?.contentMode}
					/>
				</ToolbarPortalContextProvider>
			</div>
		</AnalyticsContext>
	);
};

const ExtensionHandlerWithEditorActions = withEditorActions(ExtensionHandler);

// eslint-disable-next-line import/no-default-export
export default (props: ExtensionHandlerProps) => {
	const [contentId] = usePageContentId();
	const [isLiveViewMode, setIsLiveViewMode] = useState(() =>
		expValEquals('platform_editor_disable_view_mode_lcm_editing', 'isEnabled', true)
			? props.outerEditorApi?.editorViewMode?.sharedState.currentState()?.mode === 'view'
			: undefined,
	);

	React.useEffect(() => {
		// This marks when an LCM is rendered on the insm session.
		// We can't only rely on the node calculations which are done
		// on the editors rendered - as this does not reliably complete
		// for large pages with a many of lcms so would under detect the impact
		insm?.session?.setProperty('custom:lcm', true);
	}, []);

	useEffect(() => {
		if (!expValEquals('platform_editor_disable_view_mode_lcm_editing', 'isEnabled', true)) {
			return;
		}

		const unsubscribe = props.outerEditorApi?.editorViewMode?.sharedState.onChange(
			({ nextSharedState }) => {
				setIsLiveViewMode(nextSharedState?.mode === 'view');
			},
		);
		return unsubscribe;
	}, [props.outerEditorApi]);

	return (
		<ErrorBoundary
			attribution={Attribution.CC_EDITOR_AI}
			attributes={{ contentId, nodeLocalId: props.node?.localId }}
		>
			<OuterEditorViewContext.Provider value={props.outerEditorView}>
				<ReadOnlyLCM
					document={props.node?.parameters?.nestedContent || defaultDocument}
					contentId={contentId}
					isLiveViewMode={isLiveViewMode}
				>
					<ExtensionHandlerWithEditorActions {...props} />
				</ReadOnlyLCM>
			</OuterEditorViewContext.Provider>
		</ErrorBoundary>
	);
};
